// Product page functions

// DOM element cache for performance optimization
const DOM_CACHE = {
  shareButtons: null,
  infoSlot: null,
  infoElement: null,
  collectionSlot: null,
  collectionElement: null,
  addToCartButton: null,
  floatingCta: null,
};

// Cache DOM elements on first access
const getCachedElement = (key, selector) => {
  if (!DOM_CACHE[key]) {
    DOM_CACHE[key] = document.querySelector(selector);
  }
  return DOM_CACHE[key];
};

const getCachedElements = (key, selector) => {
  if (!DOM_CACHE[key]) {
    DOM_CACHE[key] = document.querySelectorAll(selector);
  }
  return DOM_CACHE[key];
};

/**
 * Initializes the share functionality using the Web Share API
 * Allows sharing the current page through the device's native share menu
 */
const initShareButton = () => {
  const shareButtons = getCachedElements("shareButtons", ".js-share-button");

  if (!shareButtons.length) return;

  // Check if the browser supports the Web Share API
  const canShare = navigator?.share !== undefined;

  shareButtons.forEach((button) => {
    // If the browser doesn't support sharing, hide the button
    if (!canShare) {
      button.style.display = "none";
      return;
    }

    button.addEventListener("click", async (event) => {
      event.preventDefault();

      try {
        // Sanitize and validate share data
        const url = button.dataset.shareUrl?.trim() || window.location.href;
        const title = button.dataset.shareTitle?.trim() || document.title;
        const text = button.dataset.shareText?.trim() || "";

        // Validate URL format for security
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          throw new Error("Invalid URL format");
        }

        // Invoke the native share menu
        await navigator.share({ title, text, url });
        if (window.analyticsUtils && window.analyticsUtils.handleProductShare) {
          window.analyticsUtils.handleProductShare(undefined);
        }
      } catch (error) {
        // Only log non-user-cancelled errors
        if (error.name !== "AbortError") {
          console.error("Error sharing:", error);
        }
      }
    });
  });
};

// Move info and collection to sheet slots when in mobile view
const moveElementsToSheet = () => {
  if (!matchMedia("(max-width:1023px)").matches) return;

  // Move info element
  const infoSlot = getCachedElement("infoSlot", "[data-slot-info]");
  const infoElement = getCachedElement("infoElement", ".brn-product__info");

  if (infoSlot && infoElement) {
    infoSlot.replaceChildren(infoElement);
  }

  // Move collection element
  const collectionSlot = getCachedElement(
    "collectionSlot",
    "[data-slot-collection]",
  );
  const collectionElement = getCachedElement(
    "collectionElement",
    "[data-brn-collection]",
  );

  if (collectionSlot && collectionElement) {
    const shopifySection =
      collectionElement.closest(".shopify-section") ||
      collectionElement.closest('[id^="shopify-section-"]');
    collectionSlot.replaceChildren(collectionElement);
    shopifySection?.remove();
  }

  // Update sheet height after content injection
  if (typeof window.updateSheetHeight === "function") {
    // Small delay to ensure DOM update is processed
    requestAnimationFrame(() => {
      window.updateSheetHeight();
    });
  }
};

// Add to cart button observer for floating CTA
const initFloatingCtaObserver = () => {
  const addToCartButton = getCachedElement(
    "addToCartButton",
    ".product-form__submit",
  );
  const floatingCta = getCachedElement("floatingCta", ".floating-cta");

  if (!addToCartButton || !floatingCta) {
    console.warn("Elements .product-form__submit or .floating-cta not found");
    return;
  }

  const ctaObserver = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      floatingCta.classList.toggle("hidden", entry.isIntersecting);
    },
    { threshold: 0, rootMargin: "0px" },
  );

  ctaObserver.observe(addToCartButton);
};

// Simplified Swiper initialization for synchronous product recommendations
const initCompleteLookSwiper = () => {
  const carouselElement = document.querySelector("#completeLookCarousel");

  if (!carouselElement) {
    console.warn("Complete look carousel not found");
    return;
  }

  const slides = carouselElement.querySelectorAll(".swiper-slide");

  if (slides.length === 0) {
    console.warn("No products found for complete look carousel");
    return;
  }

  try {
    new Swiper(carouselElement, {
      slidesPerView: 2.75,
      spaceBetween: 4,
      slidesOffsetBefore: 56,
      slidesOffsetAfter: 56,
      navigation: {
        nextEl: ".brn-carousel__nav--next",
        prevEl: ".brn-carousel__nav--prev",
      },
      breakpoints: {
        1024: {
          slidesPerView: 6.75,
        },
      },
    });
  } catch (error) {
    console.error("Error initializing complete look Swiper:", error);
  }
};

// --- PDP header height compensation ----------------------------------------
const setRootNegativeMarginFromHeader = () => {
  const root = document.getElementById("root");
  // Use generic selector to catch the site header
  const header = document.querySelector(".page-header");

  if (!root || !header) return;

  const updateMargin = () => {
    // Measure actual rendered height including borders
    const height = Math.ceil(header.getBoundingClientRect().height);
    // Apply negative margin to pull content up behind header or compensation
    root.style.marginTop = height ? `-${height}px` : "0px";
  };

  // Initial set
  updateMargin();

  // Watch for size changes (fonts loading, layout shifts, banners)
  const observer = new ResizeObserver(updateMargin);
  observer.observe(header);
};

// Size chart helpers (initSizeChartTabs / initSizeChartTabsFromSource /
// buildSizeChartPanesFromSource) now live in assets/size-chart.js, loaded
// globally and exposed as window.sizeChartHelpers.

// Initialize all product page functionality
/**
 * Simple loader management for recommendations
 * Shows loader by default, hides it when .brn-carousel appears
 */

document.addEventListener("DOMContentLoaded", () => {
  // Compensate initial header height so main starts under the sticky header
  setRootNegativeMarginFromHeader();

  initShareButton();
  moveElementsToSheet();
  initFloatingCtaObserver();
  initCompleteLookSwiper();
  window.sizeChartHelpers?.initSizeChartTabsFromSource();
  window.sizeChartHelpers?.buildSizeChartPanesFromSource();
  window.sizeChartHelpers?.initSizeChartTabs();
});

// Update on viewport changes (header height may change with breakpoints)
window.addEventListener("resize", setRootNegativeMarginFromHeader);
